// The pace model and the light rule. Both are Kyle's, fitted or stated, and both are marked when shown.
import type { Route } from "./types";
import { sunTimes, toMin } from "./sun.ts";

/** Fitted from seven 2026 rides: speed km/h = 26.37 − 0.320 × (metres of climb per km). An estimate, within 0.8 km/h. */
export const speedKmh = (km: number, gain: number) => 26.37 - 0.32 * (gain / km);
export const movingMin = (km: number, gain: number) => (km / speedKmh(km, gain)) * 60;

/** Minutes of riding from the start until the last unlit metre is behind him. */
export function clearDarkMin(r: Route): number | null {
  if (r.u1 == null) return null;
  return (r.u1 / speedKmh(r.km, r.gain)) * 60;
}

export const STOPS_MIN = 20;   // usual stops on a ride, added to "back about"
export const BUFFER_MIN = 40;  // his: 20 for a puncture, 20 for stopping and the phone

/** Latest start that still clears the dark before sunset with the 40 minutes on top. */
export function latestStart(r: Route, isoDate: string, lat: number, lon: number, buffer = BUFFER_MIN): number | null {
  // Marked lit: no limit from the light. Unsurveyed: the whole ride has to be done by sunset.
  const clear = clearDarkMin(r) ?? (r.lit || r.u0 === undefined ? null : movingMin(r.km, r.gain));
  if (clear == null) return null;
  const { set } = sunTimes(lat, lon, isoDate);
  return set - clear - buffer;
}

/** Weekday evening rule: clear the dark by sunset with the buffer, and finished by 21:30. Weekend: 21:30 only. */
export function fitsLight(r: Route, isoDate: string, start: string, lat: number, lon: number, latestFinish = "21:30", buffer = BUFFER_MIN, sunsetRule = true): boolean {
  const s = toMin(start);
  const back = s + movingMin(r.km, r.gain) + STOPS_MIN;
  if (back > toMin(latestFinish)) return false;
  const dow = new Date(isoDate + "T12:00:00Z").getUTCDay();
  const weekend = dow === 0 || dow === 6;
  if (weekend || !sunsetRule) return true;
  const ls = latestStart(r, isoDate, lat, lon, buffer);
  return ls == null ? true : s <= ls;
}

/** A weekday morning before work: finished by 08:45, no buffer. A puncture means starting work a little later (Kyle, 25 October). */
export function latestMorningStart(finishBy: string, rideMin: number): number {
  return toMin(finishBy) - rideMin;
}

/** On a dark morning, lit roads are fine; the unlit stretch has to wait for sunrise. */
export function unlitAfterSunrise(r: Route, isoDate: string, start: string, lat: number, lon: number): boolean {
  if (r.u0 == null) return true;
  const { rise } = sunTimes(lat, lon, isoDate);
  return toMin(start) + (r.u0 / speedKmh(r.km, r.gain)) * 60 >= rise;
}
